import { Select } from "@bafe/ui";
import { Check } from "lucide-react";
import { ChevronDown } from "reicon-react";
import { useMemo, useState } from "react";

import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "../../components/ui/command.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover.js";
import { cn } from "../../lib/utils.js";
import {
	assigneeFieldFromNames,
	assigneeNamesOf,
	assigneeSummaryLabel,
	personDisplayName,
	WORK_TYPE_OPTIONS,
	workTypeLabel,
	workTypesOf,
	type WorkTypeSlug,
} from "./hub-operation-shared.js";
import { hubToneClass, hubToneStyle, isHubStoredColor, resolveAssigneeColorValue } from "./hub-task-colors.js";

export function WorkTypeBadges({ types }: { types: string[] }) {
	if (types.length === 0) return <>—</>;
	return (
		<span className="task-table-tag-chips">
			{types.map((type) => (
				<span key={type} className="pill">
					{workTypeLabel(type)}
				</span>
			))}
		</span>
	);
}

export function WorkTypeSelect({
	id = "work-type",
	value,
	onChange,
}: {
	id?: string;
	value: string;
	onChange: (slug: string) => void;
}) {
	return (
		<Select id={id} name="workType" value={value} onChange={(event) => onChange(event.target.value)}>
			<option value="">Choose type</option>
			{WORK_TYPE_OPTIONS.map(([slug, label]) => (
				<option key={slug} value={slug}>{label}</option>
			))}
		</Select>
	);
}

export function workTypeFilterCounts(
	rows: Array<{ workTypes?: unknown }>,
): Array<[WorkTypeSlug | "", string, number]> {
	const items: Array<[WorkTypeSlug | "", string, number]> = [["", "All", rows.length]];
	for (const [value, label] of WORK_TYPE_OPTIONS) {
		const count = rows.filter((row) => workTypesOf(row).includes(value)).length;
		items.push([value, label, count]);
	}
	return items;
}

export function hubAssigneeColorMap(assigneeColors: Record<string, string | undefined>): ReadonlyMap<string, string> {
	const map = new Map<string, string>();
	for (const [key, value] of Object.entries(assigneeColors)) {
		const norm = key.trim().toLowerCase();
		const stored = value?.trim();
		if (!norm || !stored || !isHubStoredColor(stored)) continue;
		map.set(norm, stored);
	}
	return map;
}

export function WorkAssigneesCell({
	assignee,
	assigneeColorByLowerName,
}: {
	assignee: string | undefined;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
}) {
	const names = assigneeNamesOf(assignee);
	if (names.length === 0) return <>—</>;
	return (
		<div className="task-card-assignees">
			{names.map((name) => (
				<span
					key={name.toLowerCase()}
					className={`task-card-assignee-name ${hubToneClass(
						resolveAssigneeColorValue(name, assigneeColorByLowerName),
						name,
					)}`}
					style={hubToneStyle(
						resolveAssigneeColorValue(name, assigneeColorByLowerName),
						name,
					)}
				>
					{personDisplayName(name)}
				</span>
			))}
		</div>
	);
}

export function WorkAssigneesField({
	id,
	label,
	picked,
	assignable,
	onChange,
	disabled = false,
	assigneeColorByLowerName,
}: {
	id: string;
	label: string;
	picked: string[];
	assignable: string[];
	onChange: (next: string[]) => void;
	disabled?: boolean;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const summaryName = assigneeSummaryLabel(picked);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return assignable;
		return assignable.filter((name) => personDisplayName(name).toLowerCase().includes(q) || name.toLowerCase().includes(q));
	}, [assignable, query]);

	const togglePerson = (name: string) => {
		const key = name.toLowerCase();
		const on = picked.some((row) => row.toLowerCase() === key);
		onChange(on ? picked.filter((row) => row.toLowerCase() !== key) : [...picked, name]);
	};

	if (disabled) {
		return (
			<div className="hub-inventory-lookup-field flex min-w-0 flex-col gap-1.5">
				<span className="quiet" id={`${id}-label`}>{label}</span>
				<WorkAssigneesCell assignee={assigneeFieldFromNames(picked)} assigneeColorByLowerName={assigneeColorByLowerName} />
			</div>
		);
	}

	return (
		<div className="hub-inventory-lookup-field flex min-w-0 flex-col gap-1.5">
			<label id={`${id}-label`} htmlFor={id}>
				{label}
			</label>
			<div className="select hub-field-select hub-inventory-lookup-select min-w-0">
				<Popover
					open={open}
					onOpenChange={(next) => {
						setOpen(next);
						if (!next) setQuery("");
					}}
					modal={false}
				>
					<PopoverTrigger asChild>
						<button
							type="button"
							id={id}
							className="hub-field-select-trigger"
							aria-labelledby={`${id}-label`}
							aria-haspopup="listbox"
							aria-expanded={open}
						>
							<span className="truncate">{summaryName}</span>
							<ChevronDown size={12} aria-hidden="true" />
						</button>
					</PopoverTrigger>
					<PopoverContent className="hub-inventory-lookup-popover hub-work-assignees-popover p-0" align="start">
						<Command shouldFilter={false}>
							<CommandInput
								placeholder="Search people…"
								value={query}
								onValueChange={setQuery}
								aria-label={`Search ${label}`}
							/>
							<CommandList>
								{picked.length > 0 ? (
									<CommandItem
										value="__clear__"
										className="font-medium text-(--text-muted)"
										onSelect={() => onChange([])}
									>
										Clear all
									</CommandItem>
								) : null}
								{filtered.map((name) => {
									const selected = picked.some((row) => row.toLowerCase() === name.toLowerCase());
									const personTone = hubToneClass(resolveAssigneeColorValue(name, assigneeColorByLowerName), name);
									const personToneStyle = hubToneStyle(resolveAssigneeColorValue(name, assigneeColorByLowerName), name);
									return (
										<CommandItem
											key={name}
											value={name}
											className={cn("gap-2", selected && "active")}
											onSelect={() => togglePerson(name)}
										>
											<Check
												className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")}
												aria-hidden="true"
											/>
											<span className={`task-card-assignee-name min-w-0 truncate ${personTone}`} style={personToneStyle}>
												{personDisplayName(name)}
											</span>
										</CommandItem>
									);
								})}
								{filtered.length === 0 ? <CommandEmpty>No people found.</CommandEmpty> : null}
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			</div>
		</div>
	);
}

export function workAssignablePeople(
	people: Array<{ username: string }>,
	workRows: Array<{ assignee?: string }>,
): string[] {
	const seen = new Set<string>();
	const names: string[] = [];
	for (const person of people) {
		const key = person.username.trim().toLowerCase();
		if (!key || seen.has(key)) continue;
		seen.add(key);
		names.push(person.username);
	}
	for (const row of workRows) {
		for (const name of assigneeNamesOf(row.assignee)) {
			const key = name.toLowerCase();
			if (seen.has(key)) continue;
			seen.add(key);
			names.push(name);
		}
	}
	names.sort((left, right) => left.localeCompare(right));
	return names;
}

export function useWorkAssigneeColorMap(assigneeColors: Record<string, string | undefined>) {
	return useMemo(() => hubAssigneeColorMap(assigneeColors), [assigneeColors]);
}
